/**
 * Shared unread-count dot — the one count badge that sits on an icon's corner
 * (BL-1709, discharging the ADR 044 residue declared on that item).
 *
 * Collapses three class-for-class copies of the same 18px pill: bidder-web's
 * `NotificationBell` and `MessagesIcon` badges and admin-web's clerk-rail inbox
 * badge. Same collapse-N-copies-into-one shape as Spinner (BL-1124) and
 * RadioButton (BL-1243); Design-approval granted in session by Dan Saper on
 * 2026-09-13 against the three rendered call sites.
 *
 * It is deliberately NOT the admin `Badge` primitive's `count` mode: that is a
 * 24px status-label pill for table cells, and putting it on a 16px icon is
 * exactly what made the clerk-rail badge spill over the row above. This dot is
 * 18px — the smallest round shape that clears two caption digits — and
 * deliberately off the 4px scale so every consumer matches to the pixel.
 *
 * Color is a **dialect** choice per ADR 030 — `brand` reads `--brand-primary`
 * (the shared foundation token, present in both apps); `urgent` reads
 * `--bw-urgent`, bidder-web's "needs your attention" red. A caller in an app
 * whose stylesheet lacks the token gets the fallback so the dot never renders
 * transparent.
 *
 * Renders `null` at `count <= 0` so a caller can pass the raw count without its
 * own `> 0` guard, and reads `99+` past `cap` — the exact number stops meaning
 * anything there.
 */
'use client';

import clsx from 'clsx';

/** Which fill the dot takes — a dialect binding, not a semantic tone map. */
export type CountDotTone = 'brand' | 'urgent';

const TONE_STYLE: Record<CountDotTone, string> = {
    brand: 'var(--brand-primary)',
    urgent: 'var(--bw-urgent, #dc2626)',
};

export interface CountDotProps {
    /** The number to show; `<= 0` renders nothing. */
    count: number;
    /** Past this the dot reads `<cap>+`. Default 99. */
    cap?: number;
    /** Fill color dialect. Default `brand`. */
    tone?: CountDotTone;
    /**
     * Positioning classes from the caller (e.g. `absolute -right-1.5 -top-1.5`)
     * — the caller owns where the dot sits relative to its icon; the dot owns
     * everything else.
     */
    className?: string;
    /** Stable test hook. */
    'data-testid'?: string;
}

/**
 * The count dot. `aria-hidden` — the icon button it decorates carries the
 * unread total in its own accessible name, so the dot is purely visual.
 *
 * @param props - see {@link CountDotProps}.
 * @returns the dot, or `null` when there is nothing to count.
 */
export function CountDot({ count, cap = 99, tone = 'brand', className, 'data-testid': testId }: CountDotProps) {
    if (count <= 0) return null;
    return (
        <span
            aria-hidden="true"
            data-testid={testId}
            data-tone={tone}
            // `text-[11px]` rather than an app's caption token: this is a shared
            // primitive, and the two dialects' caption sizes differ (bidder 12px,
            // admin 12.5px) — 11px is the largest that keeps "99+" inside 18px.
            className={clsx(
                'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-none text-white ring-2 ring-white',
                className,
            )}
            style={{ backgroundColor: TONE_STYLE[tone] }}
        >
            {count > cap ? `${cap}+` : count}
        </span>
    );
}
